import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DriverShell from "../components/DriverShell";
import { authenticatedApi } from "../lib/api";
import { getDriverSession, saveDriverSession } from "../lib/driverSession";

function money(value) {
  return `S/ ${Number(value || 0).toFixed(2)}`;
}

function transactionDescription(transaction) {
  if (transaction?.meta?.description) return transaction.meta.description;
  if (typeof transaction?.meta === "string") {
    try {
      return JSON.parse(transaction.meta)?.description || "Movimiento de ganancias";
    } catch {
      return "Movimiento de ganancias";
    }
  }
  return "Movimiento de ganancias";
}

export default function DriverProfilePage() {
  const driver = getDriverSession();
  const [profile, setProfile] = useState(null);
  const [chart, setChart] = useState([]);
  const [tab, setTab] = useState("summary");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    if (!driver?.auth_token) return;
    try {
      const response = await authenticatedApi("/delivery/update-user-info", driver.auth_token, { method: "POST", body: "{}" });
      setProfile(response.data);
      setChart(response.chart?.chartData || response.data?.commission_account?.earnings_last_7_days || []);
      saveDriverSession({ ...driver, ...response.data });
      setError("");
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  useEffect(() => {
    load();
  }, [driver?.auth_token]);

  async function toggleStatus() {
    setBusy(true);
    try {
      const response = await authenticatedApi("/delivery/toggle-delivery-guy-status", driver.auth_token, {
        method: "POST",
        body: JSON.stringify({ toggle_status: true }),
      });
      setProfile(response.data);
      saveDriverSession({ ...driver, ...response.data });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  function toggleTheme() {
    const next = localStorage.getItem("dh_driver_dark") !== "true";
    localStorage.setItem("dh_driver_dark", String(next));
    window.location.reload();
  }

  const commission = profile?.commission_account || {};
  const maxChart = useMemo(() => Math.max(1, ...chart.map((point) => Number(point.y || 0))), [chart]);
  const initials = String(profile?.name || driver?.name || "M").split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();

  return (
    <DriverShell>
      {error && <div className="food-message food-message--error"><b>{error}</b></div>}
      {!profile ? <div className="food-message"><b>Cargando perfil…</b></div> : (
        <div className="driver-profile">
          <section className="driver-profile-hero">
            <div className="driver-profile-avatar">{profile.profile?.photo ? <img src={profile.profile.photo} alt="" /> : initials}</div>
            <div>
              <small>MI CUENTA DE REPARTO</small>
              <h1>{profile.name}</h1>
              <p>{profile.email}{profile.phone ? ` · ${profile.phone}` : ""}</p>
            </div>
            <button className={profile.status ? "is-online" : ""} disabled={busy} onClick={toggleStatus} type="button">
              {profile.status ? "● En línea" : "○ Fuera de línea"}
            </button>
          </section>

          <section className="driver-profile-services">
            <span className={profile.services?.moto ? "is-enabled" : ""}>🛵 Moto {profile.services?.moto ? "habilitada" : "desactivada"}</span>
            <span className={profile.services?.taxi ? "is-enabled" : ""}>🚕 Taxi {profile.services?.taxi ? "habilitado" : "desactivado"}</span>
            {profile.profile?.vehicle_number && <span>Placa: <b>{profile.profile.vehicle_number}</b></span>}
          </section>

          <nav className="driver-profile-tabs">
            <button className={tab === "summary" ? "is-active" : ""} onClick={() => setTab("summary")} type="button">Resumen</button>
            <button className={tab === "earnings" ? "is-active" : ""} onClick={() => setTab("earnings")} type="button">Movimientos</button>
            <button className={tab === "orders" ? "is-active" : ""} onClick={() => setTab("orders")} type="button">Pedidos</button>
            <button className={tab === "reviews" ? "is-active" : ""} onClick={() => setTab("reviews")} type="button">Reseñas</button>
          </nav>

          {tab === "summary" && (
            <>
              <section className="driver-profile-metrics">
                <article><small>GANANCIA HOY</small><strong>{money(commission.earnings_today_net)}</strong><span>Neto después de comisión</span></article>
                <article><small>GANANCIA DEL MES</small><strong>{money(commission.earnings_month_net ?? profile.totalEarnings)}</strong><span>Acumulado neto</span></article>
                <article><small>EN CURSO</small><strong>{profile.onGoingCount || 0}</strong><span>Pedidos activos</span></article>
                <article><small>COMPLETADOS</small><strong>{profile.completedCount || 0}</strong><span>Entregas realizadas</span></article>
                <article><small>COBRO COD</small><strong>{money(profile.deliveryCollection)}</strong><span>Dinero recaudado</span></article>
                <article><small>CALIFICACIÓN</small><strong>★ {profile.averageRating || "0.0"}</strong><span>{profile.ratings?.length || 0} opiniones</span></article>
              </section>
              <section className="driver-profile-chart">
                <div><h2>Ganancias de los últimos 7 días</h2><Link to="/delivery/commissions">Ver comisiones</Link></div>
                <div className="driver-profile-bars">
                  {chart.map((point, index) => (
                    <div key={`${point.x}-${index}`}><span style={{ height: `${Math.max(5, (Number(point.y || 0) / maxChart) * 100)}%` }} /><b>{point.x}</b><small>{money(point.y)}</small></div>
                  ))}
                </div>
              </section>
              <section className="driver-profile-settings">
                <button type="button" onClick={toggleTheme}>◐ Cambiar modo claro/oscuro</button>
                <Link to="/delivery/commissions">% Modalidad, saldo y comisiones</Link>
                <Link to="/delivery/completed-orders">✓ Historial completo</Link>
              </section>
            </>
          )}

          {tab === "earnings" && (
            <section className="driver-profile-list">
              {(profile.earnings || []).map((transaction) => (
                <article key={transaction.id}>
                  <div><b>{transactionDescription(transaction)}</b><small>{transaction.created_at}</small></div>
                  <strong className={transaction.type === "withdraw" ? "is-negative" : ""}>{transaction.type === "withdraw" ? "−" : "+"}{money(Number(transaction.amount || 0) / 100)}</strong>
                </article>
              ))}
              {!profile.earnings?.length && <p>No hay movimientos registrados.</p>}
            </section>
          )}

          {tab === "orders" && (
            <section className="driver-profile-list">
              {(profile.orders || []).map((entry) => (
                <article key={entry.id}>
                  <div><b>{entry.order?.unique_order_id}</b><small>{entry.order?.address}</small></div>
                  <strong>{entry.is_complete ? "Completado" : "En curso"}</strong>
                </article>
              ))}
              {!profile.orders?.length && <p>No tienes pedidos activos.</p>}
              <Link className="driver-profile-list__more" to="/delivery/completed-orders">Abrir historial completado</Link>
            </section>
          )}

          {tab === "reviews" && (
            <section className="driver-profile-list">
              {(profile.ratings || []).map((rating, index) => (
                <article key={`${rating.rating_delivery}-${index}`}>
                  <div><b>★ {Number(rating.rating_delivery || 0).toFixed(1)}</b><small>{rating.review_delivery || "Sin comentario"}</small></div>
                </article>
              ))}
              {!profile.ratings?.length && <p>Aún no tienes reseñas.</p>}
            </section>
          )}
        </div>
      )}
    </DriverShell>
  );
}
